
import React, { useState, useMemo } from "react";
import { DataTable } from "@/components/shared/DataTable";
import { FormDialog } from "@/components/shared/FormDialog";
import { VariantForm } from "./VariantForm";
import { variantColumns } from "./VariantColumns";
import { VariantsFilters } from "./VariantsFilters";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { useVariants } from "@/hooks/useVariants";
import { useFactories } from "@/hooks/useFactories";
import { useProducts } from "@/hooks/useProducts";

export function VariantsManager() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [currentVariant, setCurrentVariant] = useState<any>(null);
  
  const { variants, loading, createVariant, updateVariant, deleteVariant } = useVariants();
  const { factories, loading: loadingFactories } = useFactories();
  const { products, loading: loadingProducts } = useProducts();

  const [filters, setFilters] = useState({
    factory: "",
    product: "",
    name: ""
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      factory: "",
      product: "",
      name: ""
    });
  };

  const filteredVariants = useMemo(() => {
    return variants.filter(variant => {
      // Factory filter
      if (filters.factory) {
        const variantFactories = Array.isArray(variant.factories) ? variant.factories : [];
        const hasFactory = variantFactories.some((factoryId: string) => 
          factoryId === filters.factory || factoryId?.toString() === filters.factory?.toString()
        );
        if (!hasFactory) {
          return false;
        }
      }

      // Product filter
      if (filters.product) {
        const variantProducts = Array.isArray(variant.originalProducts || variant.products) ? (variant.originalProducts || variant.products) : [];
        const hasProduct = variantProducts.some((productId: string) => 
          productId === filters.product || productId?.toString() === filters.product?.toString()
        );
        if (!hasProduct) {
          return false;
        }
      }

      // Name filter
      if (filters.name && !variant.name?.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [variants, filters]);

  const handleAddVariant = () => {
    setCurrentVariant(null);
    setIsAddDialogOpen(true);
  };

  const handleEditVariant = (variant: any) => {
    // Use original product IDs for editing
    const editVariant = {
      ...variant,
      products: variant.originalProducts || variant.products
    };
    setCurrentVariant(editVariant);
    setIsEditDialogOpen(true);
  };

  const handleCreateSubmit = async (values: any) => {
    const success = await createVariant(values);
    if (success) setIsAddDialogOpen(false);
    return success;
  };

  const handleUpdateSubmit = async (values: any) => {
    if (!currentVariant) return false;
    const success = await updateVariant(currentVariant.id, values);
    if (success) setIsEditDialogOpen(false);
    return success;
  };

  const handleDeleteVariant = async (variant: any) => {
    return await deleteVariant(variant);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-bold">Variants</h2>
        <Button onClick={handleAddVariant}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Variant
        </Button>
      </div>

      <VariantsFilters
        factories={factories}
        products={products}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        loading={loading || loadingFactories || loadingProducts}
      />

      <DataTable
        data={filteredVariants}
        columns={variantColumns}
        onEdit={handleEditVariant}
        onDelete={handleDeleteVariant}
        title="Variants"
        itemName="Variant"
        isLoading={loading}
        searchColumns={["factories", "products"]}
      />

      {/* Add Variant Dialog */}
      <FormDialog
        title="Add New Variant"
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
      >
        <VariantForm
          onSubmit={handleCreateSubmit}
          onCancel={() => setIsAddDialogOpen(false)}
        />
      </FormDialog>

      {/* Edit Variant Dialog */}
      <FormDialog
        title="Edit Variant"
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      >
        <VariantForm
          variant={currentVariant}
          onSubmit={handleUpdateSubmit}
          onCancel={() => setIsEditDialogOpen(false)}
        />
      </FormDialog>
    </>
  );
}
